import React from 'react';
import { Link } from 'react-router-dom';
import {
  Clock,
  Info,
  MessageCircle,
  CheckCircle,
  XCircle,
  CircleDot,
  ExternalLink,
  CalendarCheck,
} from 'lucide-react';
import { formatBookingDate } from '@/lib/utils';
import { ReaderAvatar } from '@/components/brand';
import type { Reader, BookingState, ConfirmedBooking } from '@/types/reader';

const STATIC_ROOT = 'https://casting.heyjoe.io/api/s3/';

interface ReaderCardProps {
  reader: Reader;
  bookingState?: BookingState;
  confirmedBooking?: ConfirmedBooking;
  previouslyBooked?: boolean;
  isExpired?: boolean;
  onInvite: (reader: Reader) => void;
  // The legacy casting invite/book/message states. Nothing passes these any
  // more; kept optional so the card keeps rendering if something does.
  onBook?: (reader: Reader) => void;
  onCancel?: (requestId: string) => void;
  onMessage?: (reader: Reader) => void;
  onInfo: (reader: Reader) => void;
}

function readerProfilePath(reader: Reader): string {
  const norm = (s?: string) =>
    (s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  const first = norm(reader.first_name);
  const last = norm(reader.last_name);
  const slug = [first, last].filter(Boolean).join('_');
  return `/reader/${slug || reader._id}`;
}

/** Pill button used in the card footer. */
const FooterButton: React.FC<{
  variant?: 'primary' | 'ghost' | 'danger';
  onClick?: () => void;
  children: React.ReactNode;
  title?: string;
  className?: string;
  ariaLabel?: string;
}> = ({ variant = 'ghost', onClick, children, title, className, ariaLabel }) => {
  const palette = {
    primary: {
      background: 'var(--hj-blue)',
      border: '1px solid var(--hj-blue)',
      color: 'oklch(0.18 0.005 70)',
    },
    ghost: {
      background: 'transparent',
      border: '1px solid var(--hj-beige-line)',
      color: 'var(--hj-beige-ink-2)',
    },
    danger: {
      background: 'transparent',
      border: '1px solid hsl(var(--destructive) / 0.5)',
      color: 'hsl(var(--destructive))',
    },
  }[variant];
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      className={`inline-flex items-center justify-center gap-1.5 transition-colors ${className || ''}`}
      style={{
        padding: '7px 14px',
        borderRadius: 999,
        fontSize: 13,
        fontWeight: variant === 'primary' ? 600 : 500,
        fontFamily: 'inherit',
        cursor: 'pointer',
        minHeight: 44,
        ...palette,
      }}
    >
      {children}
    </button>
  );
};

export const ReaderCard: React.FC<ReaderCardProps> = ({
  reader,
  bookingState,
  confirmedBooking,
  previouslyBooked,
  isExpired,
  onInvite,
  onBook,
  onCancel,
  onMessage,
  onInfo,
}) => {
  const profile = reader.freelancer_profile;
  const response = bookingState?.response;
  const requestId = bookingState?.requestId || confirmedBooking?.requestId;
  const status = bookingState?.status;
  const totalUnread =
    (bookingState?.unreadCount || 0) + (confirmedBooking?.unreadCount || 0);

  // An invite is out but the reader hasn't responded yet (and it hasn't
  // expired) — surface an "Invited" flag so the talent doesn't double-invite.
  const pendingInvite =
    !!bookingState?.requestId && !response && status !== 'book' && !isExpired;

  const avatarUrl = reader.logo ? `${STATIC_ROOT}${reader.logo}` : null;
  const fullName = `${reader.first_name || ''} ${reader.last_name || ''}`.trim();
  // The card shows one number. It used to be the reading rate, which printed
  // "$--" for a coach who doesn't read; now it's the lowest rate they offer.
  const offered = [profile?.reader_rate, profile?.reader_director_rate, profile?.reader_coach_rate]
    .filter((r): r is number => typeof r === 'number' && r > 0);
  const headlineRate = offered.length ? Math.min(...offered) : null;
  // First ~140 characters of the About, cut at a word; the full text is one tap
  // away on the profile.
  const about = (() => {
    const t = String(profile?.reader_experience || '').replace(/\s+/g, ' ').trim();
    if (!t) return '';
    if (t.length <= 140) return t;
    const cut = t.slice(0, 140);
    return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), 100))}…`;
  })();

  // Primary action state — preserves the original invite/book/booked/decline/
  // expired flow from the previous ReaderCard.
  let primaryLabel = 'Book a session';
  let primaryAction: () => void = () => onInvite(reader);
  let primaryVariant: 'primary' | 'ghost' = 'primary';
  let statusText = '';
  let statusColor = 'var(--hj-beige-ink-3)';
  let StatusIcon: React.ComponentType<{ className?: string }> | null = null;

  if (status === 'book') {
    primaryLabel = 'Booked';
    primaryVariant = 'ghost';
    primaryAction = () => onInfo(reader);
    statusText = 'Booking confirmed';
    statusColor = 'oklch(0.42 0.15 145)';
    StatusIcon = CheckCircle;
  } else if (response === 'yes' && !isExpired) {
    primaryLabel = 'Book';
    primaryAction = () => (onBook || onInvite)(reader);
    statusText = 'Reader accepted';
    statusColor = 'oklch(0.42 0.15 145)';
    StatusIcon = CheckCircle;
  } else if (response === 'yes' && isExpired) {
    primaryLabel = 'Invite again';
    const expDate = formatBookingDate(bookingState?.requested_session_date, {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
    });
    statusText = `Expired${expDate ? ` (${expDate})` : ''}`;
    statusColor = 'oklch(0.55 0.16 75)';
    StatusIcon = XCircle;
  } else if (response === 'no') {
    statusText = 'Reader declined';
    statusColor = 'hsl(var(--destructive))';
    StatusIcon = XCircle;
  } else if (bookingState?.requestId) {
    primaryLabel = 'Invite again';
    const pendingDates = bookingState.pendingDates || [];
    if (pendingDates.length > 0) {
      const formatted = pendingDates
        .map((d) => formatBookingDate(d, { month: 'numeric', day: 'numeric', year: '2-digit' }))
        .join(', ');
      statusText = `Invite pending · ${formatted}`;
    } else {
      statusText = 'Invite pending';
    }
    statusColor = 'var(--hj-beige-ink-2)';
    StatusIcon = CircleDot;
  }

  // Upcoming session banner — shown when a confirmed booking exists AND
  // the session date is today or later. Past-dated sessions stop reading
  // as "upcoming" the day after they happen.
  const upcomingSession = (() => {
    const cb = confirmedBooking;
    const dateStr =
      cb?.requested_session_date ||
      (status === 'book' ? bookingState?.requested_session_date : undefined);
    const timeStr =
      cb?.requested_session_time ||
      (status === 'book' ? bookingState?.requested_session_time : undefined);
    if (!dateStr) return null;
    // requested_session_date is "YYYY-MM-DD" — parse as local time so a
    // booking on the current day still counts as upcoming until midnight.
    const sessionDay = new Date(`${dateStr}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (sessionDay < today) return null;
    const formatted = formatBookingDate(dateStr, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    const tz = cb?.timezone || profile?.timezone || '';
    const sessionUrl =
      cb?.sessionId && cb?.studioUri
        ? `https://casting.heyjoe.io/onboard/${cb.studioUri}/${cb.sessionId}`
        : null;
    return { formatted, timeStr, tz, sessionUrl };
  })();

  return (
    <div
      className="flex flex-col"
      style={{
        background: 'var(--hj-beige)',
        border: '1px solid var(--hj-beige-line)',
        borderRadius: 'var(--hj-r-md)',
        padding: 22,
        gap: 14,
        height: '100%',
      }}
    >
      {upcomingSession && (
        <div
          className="flex items-center gap-2 flex-wrap"
          style={{
            padding: '8px 12px',
            borderRadius: 'var(--hj-r-sm)',
            background: 'oklch(0.85 0.13 145 / 0.32)',
            border: '1px solid oklch(0.55 0.16 145)',
            color: 'var(--hj-beige-ink)',
            fontSize: 12.5,
          }}
        >
          <CalendarCheck size={14} style={{ color: 'oklch(0.42 0.15 145)' }} />
          <span style={{ fontWeight: 600 }}>Upcoming session</span>
          <span style={{ color: 'var(--hj-beige-ink-2)' }}>
            {upcomingSession.formatted}
            {upcomingSession.timeStr ? ` · ${upcomingSession.timeStr}` : ''}
            {upcomingSession.tz ? ` ${upcomingSession.tz}` : ''}
          </span>
          {upcomingSession.sessionUrl && (
            <a
              href={upcomingSession.sessionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-1 hover:underline"
              style={{ color: 'oklch(0.42 0.15 145)', fontWeight: 600 }}
            >
              Join <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}

      {/* Header — avatar + identity + rate */}
      <div className="flex items-start gap-3.5 sm:gap-4">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={fullName}
            className="hj-headshot object-cover flex-shrink-0"
            style={{ width: 64, height: 78 }}
          />
        ) : (
          <ReaderAvatar name={fullName || 'Reader'} size={64} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3
              className="m-0"
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: 'var(--hj-beige-ink)',
                lineHeight: 1.2,
              }}
            >
              {fullName || 'Reader'}
            </h3>
            {profile?.featured && (
              <span
                className="mono-label"
                style={{
                  padding: '2px 7px',
                  borderRadius: 999,
                  background: 'var(--hj-yellow)',
                  color: 'oklch(0.2 0.05 80)',
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  transform: 'rotate(-2deg)',
                }}
              >
                Featured
              </span>
            )}
            {previouslyBooked && !profile?.featured && (
              <span
                className="mono-label"
                style={{
                  padding: '2px 7px',
                  borderRadius: 999,
                  background: 'var(--hj-pink-tint)',
                  color: 'oklch(0.42 0.15 350)',
                  fontSize: 9,
                }}
              >
                Previously booked
              </span>
            )}
            {pendingInvite && (
              <span
                className="mono-label inline-flex items-center gap-1"
                style={{
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: 'var(--hj-blue)',
                  color: 'oklch(0.18 0.005 70)',
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                }}
              >
                <CircleDot className="w-2.5 h-2.5" />
                Invited
              </span>
            )}
          </div>
          {profile?.timezone && (
            <div
              className="flex items-center gap-1"
              style={{ fontSize: 12, color: 'var(--hj-beige-ink-3)', marginTop: 4 }}
            >
              <Clock size={11} />
              {profile.timezone}
            </div>
          )}
          {statusText && (
            <div
              className="flex items-center gap-1"
              style={{ fontSize: 12.5, marginTop: 6, color: statusColor }}
            >
              {StatusIcon && <StatusIcon className="w-3.5 h-3.5" />}
              <span>{statusText}</span>
            </div>
          )}
        </div>
        {headlineRate != null && (
          <div className="flex-shrink-0 text-right">
            <div
              className="tabular"
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: 'var(--hj-beige-ink)',
                lineHeight: 1,
                fontFamily: 'var(--hj-font-mono)',
              }}
            >
              ${headlineRate.toFixed(0)}
            </div>
            <div style={{ fontSize: 10, color: 'var(--hj-beige-ink-3)', marginTop: 4 }}>
              / 15 min
            </div>
          </div>
        )}
      </div>

      {/* The reader's About, on the card. This only ever showed availability
          notes, which most readers leave blank, so a card with a 20-year bio
          behind it rendered as a name and a price. */}
      {about && (
        <p
          className="line-clamp-3"
          style={{
            margin: 0,
            fontSize: 13,
            color: 'var(--hj-beige-ink)',
            lineHeight: 1.5,
          }}
        >
          {about}
        </p>
      )}
      {profile?.reader_availability_notes && (
        <p
          className="line-clamp-2"
          style={{
            margin: 0,
            fontSize: 12.5,
            color: 'var(--hj-beige-ink-2)',
            lineHeight: 1.5,
          }}
        >
          {profile.reader_availability_notes}
        </p>
      )}

      {/* Categories */}
      {profile?.reader_categories && profile.reader_categories.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {profile.reader_categories.map((cat) => (
            <span
              key={cat}
              style={{
                padding: '3px 10px',
                borderRadius: 999,
                background: 'var(--hj-beige-deep)',
                border: '1px solid var(--hj-beige-line)',
                fontSize: 11.5,
                color: 'var(--hj-beige-ink-2)',
              }}
            >
              {cat}
            </span>
          ))}
        </div>
      )}

      {/* Extra rates surfaced if Director / Coach are set */}
      {(profile?.reader_director_rate || profile?.reader_coach_rate) && (
        <div
          className="flex flex-wrap gap-3"
          style={{
            paddingTop: 10,
            borderTop: '1px solid var(--hj-beige-line)',
            fontSize: 12,
            color: 'var(--hj-beige-ink-3)',
          }}
        >
          {profile.reader_director_rate != null && (
            <span>
              Director{' '}
              <span className="tabular" style={{ color: 'var(--hj-beige-ink-2)', fontFamily: 'var(--hj-font-mono)', fontWeight: 600 }}>
                ${profile.reader_director_rate.toFixed(0)}
              </span>
            </span>
          )}
          {profile.reader_coach_rate != null && (
            <span>
              Coach{' '}
              <span className="tabular" style={{ color: 'var(--hj-beige-ink-2)', fontFamily: 'var(--hj-font-mono)', fontWeight: 600 }}>
                ${profile.reader_coach_rate.toFixed(0)}
              </span>
            </span>
          )}
        </div>
      )}

      {/* Footer actions */}
      <div
        className="flex flex-wrap items-center gap-2 mt-auto"
        style={{ paddingTop: 12, borderTop: '1px solid var(--hj-beige-line)' }}
      >
        <FooterButton variant={primaryVariant} onClick={primaryAction}>
          {primaryLabel}
        </FooterButton>

        {bookingState?.requestId && status !== 'book' && onCancel && (
          <FooterButton variant="danger" onClick={() => onCancel(bookingState.requestId)}>
            {response ? 'Withdraw' : 'Cancel'}
          </FooterButton>
        )}

        {requestId && onMessage && (
          <button
            type="button"
            onClick={() => onMessage(reader)}
            aria-label="Message reader"
            className="relative inline-flex items-center justify-center"
            style={{
              width: 44,
              height: 44,
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--hj-beige-line)',
              color: 'var(--hj-beige-ink-2)',
              cursor: 'pointer',
            }}
          >
            <MessageCircle className="w-4 h-4" />
            {totalUnread > 0 && (
              <span
                className="absolute"
                style={{
                  top: -4,
                  right: -4,
                  background: 'hsl(var(--destructive))',
                  color: 'white',
                  fontSize: 10,
                  fontWeight: 700,
                  borderRadius: 999,
                  width: 18,
                  height: 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {totalUnread}
              </span>
            )}
          </button>
        )}

        {/* Labelled, not an icon on its own — an unlabelled glyph gives no one a
            reason to press it, so the bio never got opened. */}
        <button
          type="button"
          onClick={() => onInfo(reader)}
          className="inline-flex items-center justify-center gap-1.5"
          style={{
            height: 44,
            padding: '0 16px',
            borderRadius: 999,
            background: 'transparent',
            border: '1px solid var(--hj-beige-line)',
            color: 'var(--hj-beige-ink-2)',
            fontSize: 12.5,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <Info className="w-4 h-4" /> About
        </button>

        <Link
          to={readerProfilePath(reader)}
          target="_blank"
          aria-label="View public profile"
          className="inline-flex items-center justify-center"
          style={{
            width: 44,
            height: 44,
            borderRadius: 999,
            background: 'transparent',
            border: '1px solid var(--hj-beige-line)',
            color: 'var(--hj-beige-ink-2)',
            textDecoration: 'none',
          }}
        >
          <ExternalLink className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};